import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'react-hot-toast';

// Import constants
import { INITIAL_END_HOURS, INITIAL_START_HOURS } from '../constants/schedule';

// Import store
import useScheduleStore from '../store/scheduleStore';

// Import utils
import { generateRoomId } from '../utils/schedule';
import { scrollToId } from '../utils/scroll';
import { normalizeTimeString } from '../utils/date';
import { createRoomLink } from '../utils/linking';
import { saveStoredPlanner } from '../utils/schedule-storage';

// Import hooks
import useEventModal from '../hooks/schedule/useEventModal';

// Import components
import SeoHead from '../components/seo/SeoHead';
import JsonLd from '../components/seo/JsonLd';
import { getLandingPageJsonLd } from '../components/seo/structuredData';
import StickyNavbar from '../components/navbar/StickyNavbar';
import EventModal from '../components/schedule/EventModal';
import DisplayAd from '../components/ads/DisplayAd';
import Hero from '../components/landing/Hero';
import Preview from '../components/landing/Preview';
import Features from '../components/landing/Features';
import FeatureVideos from '../components/landing/FeatureVideos';
import Pricing from '../components/landing/Pricing';
import HowToUse from '../components/landing/HowToUse';
import FAQ from '../components/landing/FAQ';
import Download from '../components/landing/Download';
import Footer from '../components/landing/Footer';

export default function LandingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [modalInitialTab, setModalInitialTab] = useState<'create' | 'saved'>(
    'create',
  );
  const [modalOpenKey, setModalOpenKey] = useState(0);

  // Modal state for creating a new event
  const {
    showPopup,
    openPopup,
    cancelPopup,
    closePopup,
    eventTitle,
    setEventTitle,
    eventTimezone,
    setEventTimezone,
    pickerRange,
    setPickerRange,
    pickerStart,
    setPickerStart,
    pickerEnd,
    setPickerEnd,
  } = useEventModal(INITIAL_START_HOURS, INITIAL_END_HOURS);
  const resetRoom = useScheduleStore((s) => s.resetRoom);

  const handleOpenModal = () => {
    // Fully clear previous room state so the incoming init replaces everything
    resetRoom();
    setModalInitialTab('create');
    setModalOpenKey((k) => k + 1);

    if (openPopup) openPopup();
  };

  const handleOpenSavedPlanners = () => {
    resetRoom();
    setModalInitialTab('saved');
    setModalOpenKey((k) => k + 1);
    if (openPopup) openPopup();
  };

  const handleOpenSavedPlanner = (id: string) => {
    closePopup();
    navigate(`/s/${id}`);
  };

  const handleSaveFromModal = () => {
    if (pickerRange.from === undefined || pickerRange.to === undefined) {
      toast.error('Please select a date range from the date picker');
      return;
    }

    // Validate start/end times are different
    if (pickerStart === pickerEnd) {
      toast.error('Start and end times cannot be the same');
      return;
    }

    // Validate a time format (HH:mm) from the time picker
    const sParts = pickerStart.split(':');
    const eParts = pickerEnd.split(':');
    const sh = Number(sParts[0]);
    const sm = Number(sParts[1]);
    const eh = Number(eParts[0]);
    const em = Number(eParts[1]);
    const startM = sh * 60 + sm;
    const endM = eh * 60 + em;

    // Validate time range (start before the end)
    if (endM <= startM) {
      toast.error('End time must be later than start time');
      return;
    }

    // Close the modal
    closePopup();

    // Navigate to a new schedule room, passing initial config via navigation state.
    // Normalize time strings to HH:MM format (remove seconds if present)
    const id = generateRoomId();
    const trimmedTitle = eventTitle?.trim();
    const url = createRoomLink(id);

    void saveStoredPlanner({
      id: id,
      url: url,
      title: trimmedTitle || undefined,
      createdAt: new Date().toISOString(),
    });

    navigate(`/s/${id}`, {
      state: {
        init: {
          title: eventTitle || undefined,
          start: normalizeTimeString(pickerStart),
          end: normalizeTimeString(pickerEnd),
          range: {
            from: pickerRange.from?.toISOString(),
            to: pickerRange.to?.toISOString(),
          },
          timezone: eventTimezone,
        },
      },
    });
  };

  // Scroll to a section if a hash fragment is present (e.g., /#faqs or /#how-to-use)
  useEffect(() => {
    const hash = location.hash?.replace('#', '');
    if (!hash) return;
    // Delay to allow layout/sections to mount
    const t = window.setTimeout(() => {
      scrollToId(hash);
    }, 0);
    return () => window.clearTimeout(t);
  }, [location.hash]);

  return (
    <div className="w-full min-h-screen bg-white text-gray-800">
      <SeoHead route="/" />
      <JsonLd data={getLandingPageJsonLd()} />
      <StickyNavbar />

      {/* Hero with action to open the create event modal */}
      <Hero
        onStartClick={handleOpenModal}
        onViewSavedClick={handleOpenSavedPlanners}
      />

      <Preview />

      <div className="mx-auto">
        <DisplayAd />
      </div>

      <Features />

      <HowToUse />

      <div className="mx-auto">
        <DisplayAd />
      </div>

      <FeatureVideos />

      <Pricing />

      <div className="mx-auto">
        <DisplayAd />
      </div>

      <FAQ />

      <div className="mx-auto">
        <DisplayAd />
      </div>

      <Download />

      <Footer />

      {/* Create event modal */}
      <EventModal
        key={modalOpenKey}
        isOpen={showPopup}
        isOwner={true}
        title={eventTitle}
        onTitleChange={setEventTitle}
        timezone={eventTimezone}
        onTimezoneChange={setEventTimezone}
        pickerRange={pickerRange}
        onPickerRangeChange={setPickerRange}
        pickerStart={pickerStart}
        onPickerStartChange={setPickerStart}
        pickerEnd={pickerEnd}
        onPickerEndChange={setPickerEnd}
        onClose={cancelPopup}
        onSave={handleSaveFromModal}
        showSavedPlanners={true}
        initialTab={modalInitialTab}
        onOpenSavedPlanner={handleOpenSavedPlanner}
      />
    </div>
  );
}
